import React from "react";
import { toast as sonner } from "sonner";
import { CircleCheck, CircleAlert, TriangleAlert, Info, X } from "lucide-react";

// THE one toast surface for the whole app. Every toast renders through here, so the brand look is
// defined exactly once. Two lines: a mono state LABEL over an IBM Plex body message, a 3px accent
// rule down the left edge, square corners, no shadow. Never pure #FFFFFF anywhere.
//
// Existing call sites keep working unchanged — `toast.success("message")` gets the default per-type
// label ("SUCCESS"). Pass `{ label: "PAYOUTS ENABLED" }` to override the label line.
// Every toast AUTO-DISMISSES (errors/warnings linger a little longer so they're readable), and can
// always be dismissed early via the X. `toast.loading` is the only persistent one (cleared in code).
const CFG = {
  success: { color: "#8FA84A", Icon: CircleCheck, label: "SUCCESS", duration: 5000 },
  error: { color: "#D9705A", Icon: CircleAlert, label: "ERROR", duration: 7000 },
  warning: { color: "#C2600F", Icon: TriangleAlert, label: "WARNING", duration: 7000 },
  info: { color: "#1F6BA8", Icon: Info, label: "INFO", duration: 5000 },
  message: { color: "#76797D", Icon: Info, label: "NOTICE", duration: 5000 },
};

function ToastBody({ id, type, label, message }) {
  const c = CFG[type] || CFG.message;
  const Icon = c.Icon;
  return (
    <div
      className="os-toast"
      data-testid={`toast-${type}`}
      style={{
        position: "relative",
        background: "#111213",
        border: "1px solid #2E2F31",
        borderLeft: `3px solid ${c.color}`,
        padding: "14px 30px 15px 15px",
        maxWidth: 380,
        width: "100%",
        borderRadius: 0,
        boxShadow: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <Icon size={13} strokeWidth={1.9} style={{ color: c.color, marginTop: 1, flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: "9.5px",
              letterSpacing: "0.11em",
              textTransform: "uppercase",
              color: c.color,
            }}
          >
            {label || c.label}
          </div>
          <div
            style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontSize: "13px",
              lineHeight: 1.5,
              color: "#DAD6CE",
              marginTop: 6,
            }}
          >
            {message}
          </div>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        data-testid="toast-dismiss"
        onClick={() => sonner.dismiss(id)}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#DAD6CE")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#76797D")}
        style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none", padding: 0, cursor: "pointer", color: "#76797D", lineHeight: 0 }}
      >
        <X size={12} strokeWidth={2} />
      </button>
    </div>
  );
}

function show(type, message, opts = {}) {
  const c = CFG[type] || CFG.message;
  const { label, duration, ...rest } = opts;
  return sonner.custom((id) => <ToastBody id={id} type={type} label={label} message={message} />, {
    // Success + info auto-dismiss after 6s; errors + warnings stay until dismissed. Overridable.
    duration: duration ?? (c.sticky ? Infinity : 6000),
    unstyled: true,
    ...rest,
  });
}

function toast(message, opts) {
  return show("message", message, opts);
}
toast.success = (m, o) => show("success", m, o);
toast.error = (m, o) => show("error", m, o);
toast.warning = (m, o) => show("warning", m, o);
toast.info = (m, o) => show("info", m, o);
toast.message = (m, o) => show("message", m, o);
toast.loading = (m, o) => show("info", m, { duration: Infinity, ...o });
toast.dismiss = (id) => sonner.dismiss(id);
toast.custom = (...args) => sonner.custom(...args);
toast.promise = (...args) => sonner.promise(...args);

export { toast };
export default toast;
